import { SearchCreditClassItem } from '@/@types/client-api';
import { FC, useMemo, useState } from 'react';
import { CreditDetails, RowInfo } from '../PDP/ProgramCredit/ViewCreditsForAllJurisdiction';
import Accordion from '../Accordion';
import AllJurisdictionsTable from '../Tables/AllJurisdictionsTable';
import classnames from 'classnames';
import Icon from '../Icon';
import Text from '@/components/cms/Text';
import ComplexTooltip from '../Tooltip/ComplexTooltip';

type InlineDebugInformationProps = {
  credits: SearchCreditClassItem[];
  jurisdictionId: string;
  className?: string;
  pk?: string;
  isPersonalizedItem?: boolean;
};

const InlineDebugInformation: FC<InlineDebugInformationProps> = ({
  credits,
  jurisdictionId,
  className,
  pk,
  isPersonalizedItem
}) => {
  const [contentContainer, setContentContainer] = useState<HTMLDivElement | null>(null);

  const mapCreditStatus = (value: number) => {
    switch (value) {
      case 1:
        return 'Approved';
      case 2:
        return 'Pending';
      case 3:
        return 'NotApproved';
      case 4:
        return 'Expired';
      case 5:
        return 'CreditUponRequest';
      case 6:
        return 'NonMcleState';
      case 7:
        return 'ApplicationSubmitted';
    }
  };

  const renderTableRows = useMemo((): RowInfo[] => {
    return credits
      .flatMap(a => a.jurisdictions)
      ?.filter(a => a?.jurisdiction?.toLowerCase() == jurisdictionId?.toLowerCase())
      .map((j, i) => {
        const sum = j?.types?.reduce((prev, next) => prev + (next?.qty ?? 0), 0);
        return {
          key: i,
          jurisdiction: j?.description ?? '',
          statusDescription: '',
          statusEnum: mapCreditStatus(j?.status ?? -1),
          total: sum ?? 0,
          creditDetails: j?.types?.map(a => {
            return {
              creditOffered: a.qty ?? 0,
              typeTitle: a.description ?? a.type
            } as CreditDetails;
          }),
          isTransitional: j?.jurisdiction?.endsWith('-TR'),
          regionSk: 0
        } as RowInfo;
      });
  }, [credits, jurisdictionId]);

  const creditsSection = useMemo(() => {
    return (
      <div>
        <Text className="text-1-medium pr-6">Credits</Text>
        <div className="mb-4 [&>[data-component='AllJurisdictionsTable']]:!bg-white">
          <AllJurisdictionsTable
            tableHeader={['Jurisdiction', 'Status', 'Total', 'Credit details']}
            rows={renderTableRows}
          />
        </div>
      </div>
    );
  }, [renderTableRows]);

  const personalizedTooltip = useMemo(() => {
    return {
      children: (
        <>
          <ComplexTooltip.Trigger className="z-1">
            <Icon name="profile" size="medium" className="mr-2" />
          </ComplexTooltip.Trigger>

          <ComplexTooltip.Content customContainer={contentContainer}>
            This item is sourced from the user's "For You" personalized table.
          </ComplexTooltip.Content>
        </>
      )
    };
  }, [contentContainer]);

  return (
    <div className="relative mt-3 overflow-hidden" key={`${pk}-inline-debug`}>
      <div
        className={classnames(
          'mt-6 flex items-center rounded p-[0.99px]',
          'bg-gradient-to-tr from-[#FFC600] via-red to-blue-400',
          'origin-top duration-long animate-in fade-in-0 slide-in-from-top'
        )}
      >
        <Accordion
          type="single"
          classNames={classnames(
            '!m-0 flex flex-col rounded-[4px] bg-gray-feather px-4',
            className
          )}
          headerClassNames="w-fit"
          items={[
            {
              id: `${pk}-inline-debug-accordion-item`,
              className: `group/result-${jurisdictionId}-accordion`,
              header: (
                <span className="text-2-bold mr-2 flex flex-row items-center justify-start pr-6">
                  <Icon name="info" size="medium" className="mr-2" />
                  {!!isPersonalizedItem && (
                    <ComplexTooltip {...personalizedTooltip} variant="light" />
                  )}
                  <span>
                    Metadata (variation: <span className="italic">{pk}</span>)
                  </span>
                </span>
              ),
              content: [<div className="mb-2">{creditsSection}</div>]
            }
          ]}
        />
      </div>
      <div ref={setContentContainer} />
    </div>
  );
};

export default InlineDebugInformation;
